class LibertasCartDrawer extends HTMLElement {
  connectedCallback() {
    if (this.parentElement !== document.body) {
      const existingDrawer = [...document.body.querySelectorAll('[data-cart-drawer]')]
        .find((drawer) => drawer.dataset.sectionId === this.dataset.sectionId);
      if (existingDrawer && existingDrawer !== this) existingDrawer.remove();
      document.body.append(this);
      return;
    }

    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';
    this.panel = this.querySelector('.lib-cart-drawer__panel');
    this.lastFocusedElement = null;
    this.listenerController = new AbortController();
    const { signal } = this.listenerController;

    this.addEventListener('click', (event) => this.handleClick(event), { signal });
    this.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.id === `shopify-section-${this.dataset.sectionId}`) this.remove();
    }, { signal });
  }

  disconnectedCallback() {
    this.listenerController?.abort();
    this.listenerController = null;
    delete this.dataset.ready;
  }

  open(trigger = document.activeElement) {
    window.libertasPrepareScrollLock?.();
    this.lastFocusedElement = trigger;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => this.panel?.focus());
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    this.lastFocusedElement?.focus?.();
  }

  async handleClick(event) {
    if (event.target.closest('[data-cart-close]')) {
      this.close();
      return;
    }

    const line = event.target.closest('[data-cart-line]');
    if (!line) return;

    if (event.target.closest('[data-cart-remove]')) {
      await this.changeLine(line.dataset.lineKey, 0);
      return;
    }

    const quantityButton = event.target.closest('[data-cart-quantity]');
    if (!quantityButton) return;
    const currentQuantity = Number(line.dataset.quantity);
    const nextQuantity = quantityButton.dataset.cartQuantity === 'increase'
      ? currentQuantity + 1
      : Math.max(0, currentQuantity - 1);
    await this.changeLine(line.dataset.lineKey, nextQuantity);
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if (event.key !== 'Tab' || !this.classList.contains('is-open')) return;

    const focusable = [...this.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )].filter((element) => element.getClientRects().length > 0);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  async changeLine(key, quantity) {
    if (this.classList.contains('is-loading')) return;
    this.setLoading(true);

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!response.ok) throw new Error(await this.getErrorMessage(response));
      await this.refresh();
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      this.showError(error.message);
    } finally {
      this.setLoading(false);
    }
  }

  async refresh() {
    const sectionId = this.dataset.sectionId;
    const response = await fetch(
      `${window.Shopify.routes.root}cart?sections=${encodeURIComponent(sectionId)}`
    );
    if (!response.ok) throw new Error('No se pudo actualizar el carrito.');

    const sections = await response.json();
    const html = sections[sectionId] || Object.values(sections)[0];
    if (!html) throw new Error('No se pudo actualizar el carrito.');

    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const nextDrawer = documentFragment.querySelector('[data-cart-drawer]');
    const nextContent = nextDrawer?.querySelector('[data-cart-drawer-content]');
    if (!nextContent) throw new Error('No se pudo actualizar el carrito.');

    this.querySelector('[data-cart-drawer-content]').innerHTML = nextContent.innerHTML;
    const itemCount = nextDrawer.querySelector('[data-cart-drawer-count]')?.textContent || '0';
    document.querySelectorAll('[data-cart-count]').forEach((counter) => {
      counter.textContent = itemCount;
    });
  }

  setLoading(loading) {
    this.classList.toggle('is-loading', loading);
    this.toggleAttribute('aria-busy', loading);
  }

  showError(message) {
    const error = this.querySelector('[data-cart-error]');
    if (!error) return;
    error.textContent = message || 'Ha ocurrido un error. Inténtalo de nuevo.';
    error.hidden = false;
  }

  async getErrorMessage(response) {
    const responseText = await response.text();
    try {
      const payload = JSON.parse(responseText);
      return payload.description || payload.message || 'Ha ocurrido un error. Inténtalo de nuevo.';
    } catch {
      return responseText.trim() || 'Ha ocurrido un error. Inténtalo de nuevo.';
    }
  }
}

if (!customElements.get('libertas-cart-drawer')) {
  customElements.define('libertas-cart-drawer', LibertasCartDrawer);
}

if (!window.libertasCartEventsReady) {
  window.libertasCartEventsReady = true;

  document.addEventListener('click', (event) => {
    const cartLink = event.target.closest('[data-cart-trigger]');
    if (!cartLink || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    event.preventDefault();
    drawer.open(cartLink);
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer || drawer.classList.contains('is-loading')) return;

    event.preventDefault();
    const submitter = event.submitter;
    const offers = form.closest('libertas-product')?.querySelector('libertas-quantity-offers');
    const tabiSet = form.closest('libertas-product')?.querySelector('libertas-tabi-set');
    const formData = offers?.buildFormData ? offers.buildFormData(form) : tabiSet?.appendToFormData ? tabiSet.appendToFormData(form) : new FormData(form);
    if (!formData) return;
    submitter?.setAttribute('disabled', '');
    drawer.setLoading(true);

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      if (!response.ok) throw new Error(await drawer.getErrorMessage(response));
      await drawer.refresh();
      drawer.open(submitter);
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (error) {
      drawer.open(submitter);
      drawer.showError(error.message);
    } finally {
      drawer.setLoading(false);
      submitter?.removeAttribute('disabled');
    }
  });
}
